import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  IconButton,
  Typography,
} from "@mui/material";
import CameraAltIcon from "@mui/icons-material/CameraAlt";
import PhotoLibraryIcon from "@mui/icons-material/PhotoLibrary";
import DeleteIcon from "@mui/icons-material/Delete";
import { compressImage } from "utils/fileUtils";

// Photo-only evidence dialog for the fragile-package check (transit/final station).
// Video is intentionally NOT captured here — the blocking alert shown before this
// dialog opens instructs staff to send the inspection video to the chat group and
// report to the manager; `videoReportConfirmed` just records that they did.
const FragileCheckDialog = ({
  open,
  onClose,
  onSubmit, // (files: File[], videoReportConfirmed: boolean) => Promise<void>
  tracking,
  stage, // "transit" | "final"
  location,
  setLocation,
  isUploading = false,
  uploadProgress = null,
}) => {
  const { t } = useTranslation();
  const cameraInputRef = useRef(null);
  const galleryInputRef = useRef(null);
  const [photos, setPhotos] = useState([]); // [{ file, previewUrl }]
  const [videoReportConfirmed, setVideoReportConfirmed] = useState(false);
  const [isCompressing, setIsCompressing] = useState(false);

  useEffect(() => {
    if (open) {
      setPhotos([]);
      setVideoReportConfirmed(false);
      if (navigator.geolocation) {
        navigator.geolocation.getCurrentPosition(
          (pos) => {
            setLocation({ lat: pos.coords.latitude, lng: pos.coords.longitude });
          },
          () => setLocation({ lat: null, lng: null }),
          { enableHighAccuracy: true }
        );
      }
    }
  }, [open, setLocation]);

  if (!open) return null;

  const addFiles = async (fileList) => {
    const incoming = Array.from(fileList || []).filter((f) => f.type.startsWith("image/"));
    if (incoming.length === 0) return;
    setIsCompressing(true);
    try {
      const compressed = await Promise.all(
        incoming.map(async (file) => {
          const blob = await compressImage(file, 0.8, 1024);
          const compressedFile = blob
            ? new File([blob], file.name || `photo_${Date.now()}.jpg`, { type: "image/jpeg" })
            : file;
          return { file: compressedFile, previewUrl: URL.createObjectURL(compressedFile) };
        })
      );
      setPhotos((prev) => [...prev, ...compressed]);
    } finally {
      setIsCompressing(false);
    }
  };

  const removePhoto = (idx) => {
    setPhotos((prev) => {
      const next = [...prev];
      const [removed] = next.splice(idx, 1);
      if (removed) URL.revokeObjectURL(removed.previewUrl);
      return next;
    });
  };

  const canSubmit = photos.length > 0 && videoReportConfirmed && !isUploading && !isCompressing;

  return (
    <Box
      sx={{
        position: "fixed",
        inset: 0,
        width: "100vw",
        height: "100vh",
        background: "rgba(0,0,0,0.92)",
        zIndex: 1300,
        display: "flex",
        flexDirection: "column",
        color: "#fff",
      }}
    >
      {(isUploading || isCompressing) && (
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            backgroundColor: "rgba(0,0,0,0.8)",
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 1500,
          }}
        >
          <CircularProgress
            variant={isUploading && uploadProgress != null ? "determinate" : "indeterminate"}
            value={uploadProgress ?? 0}
            size={70}
            thickness={4}
            sx={{ color: "#fff", mb: 2 }}
          />
          <Typography variant="h6" color="#fff">
            {isUploading
              ? uploadProgress != null
                ? `${t("scan.fragileCheck.uploading")} ${uploadProgress}%`
                : t("scan.fragileCheck.uploading")
              : t("scan.fragileCheck.optimizing")}
          </Typography>
        </Box>
      )}

      {/* Header */}
      <Box sx={{ p: 2, textAlign: "center" }}>
        <Typography variant="h6" sx={{ fontWeight: 700, wordBreak: "break-all" }}>
          {tracking}
        </Typography>
        <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.75)", mt: 0.5 }}>
          {stage === "transit"
            ? t("scan.fragileCheck.dialogTitleTransit")
            : t("scan.fragileCheck.dialogTitleFinal")}
        </Typography>
      </Box>

      {/* Photo grid */}
      <Box
        sx={{
          flex: 1,
          overflowY: "auto",
          px: 2,
          display: "grid",
          gridTemplateColumns: "repeat(auto-fill, minmax(96px, 1fr))",
          gap: 1.5,
          alignContent: "start",
        }}
      >
        {photos.map((p, idx) => (
          <Box
            key={p.previewUrl}
            sx={{ position: "relative", borderRadius: 1.5, overflow: "hidden", aspectRatio: "1 / 1" }}
          >
            <img
              src={p.previewUrl}
              alt={`fragile-check-${idx}`}
              style={{ width: "100%", height: "100%", objectFit: "cover" }}
            />
            <IconButton
              size="small"
              onClick={() => removePhoto(idx)}
              sx={{
                position: "absolute",
                top: 2,
                right: 2,
                backgroundColor: "rgba(0,0,0,0.6)",
                color: "#fff",
                "&:hover": { backgroundColor: "rgba(0,0,0,0.8)" },
              }}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Box>
        ))}
      </Box>

      {/* Hidden inputs */}
      <input
        type="file"
        accept="image/*"
        capture="environment"
        ref={cameraInputRef}
        onChange={(e) => {
          addFiles(e.target.files);
          e.target.value = "";
        }}
        style={{ display: "none" }}
      />
      <input
        type="file"
        accept="image/*"
        multiple
        ref={galleryInputRef}
        onChange={(e) => {
          addFiles(e.target.files);
          e.target.value = "";
        }}
        style={{ display: "none" }}
      />

      {/* Footer */}
      <Box sx={{ p: 2, display: "flex", flexDirection: "column", gap: 1.5 }}>
        <Box sx={{ display: "flex", justifyContent: "center", gap: 2 }}>
          <IconButton
            onClick={() => cameraInputRef.current?.click()}
            sx={{ backgroundColor: "#fff", width: 64, height: 64, "&:hover": { backgroundColor: "#eee" } }}
          >
            <CameraAltIcon sx={{ fontSize: 32, color: "#4d4d4d" }} />
          </IconButton>
          <IconButton
            onClick={() => galleryInputRef.current?.click()}
            sx={{ backgroundColor: "#fff", width: 64, height: 64, "&:hover": { backgroundColor: "#eee" } }}
          >
            <PhotoLibraryIcon sx={{ fontSize: 32, color: "#4d4d4d" }} />
          </IconButton>
        </Box>

        <Typography variant="caption" sx={{ textAlign: "center", color: "rgba(255,255,255,0.6)" }}>
          {t("scan.fragileCheck.minPhotoHint", { count: photos.length })}
        </Typography>

        <FormControlLabel
          sx={{ color: "#fff", mx: 0 }}
          control={
            <Checkbox
              checked={videoReportConfirmed}
              onChange={(e) => setVideoReportConfirmed(e.target.checked)}
              sx={{ color: "#fff" }}
            />
          }
          label={
            <Typography variant="body2" sx={{ color: "#fff" }}>
              {t("scan.fragileCheck.videoConfirmLabel")}
            </Typography>
          }
        />

        <Button
          variant="contained"
          color="secondary"
          disabled={!canSubmit}
          onClick={() => onSubmit(photos.map((p) => p.file), videoReportConfirmed)}
        >
          {t("scan.fragileCheck.submit")}
        </Button>
      </Box>
    </Box>
  );
};

export default FragileCheckDialog;
